import type { ReactNode } from "react";
import { AppError, Button, EmptyState, Loader } from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import type { TFunction } from "i18next";

import styles from "./paged-frame.module.css";

type Props = {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyText: string;
  page: number;
  hasMore: boolean;
  isFetching: boolean;
  onPage: (page: number) => void;
  testRole: string;
  /** Sits above the rows, and only while there are rows. */
  toolbar?: ReactNode;
  t: TFunction;
  children: ReactNode;
};

/**
 * What every drawer tab shares around its own table: the loading and failure
 * states, the empty message, and the pager. The tabs differ only in their
 * columns, so they say nothing about any of this themselves.
 */
export function PagedFrame({
  isLoading,
  isError,
  isEmpty,
  emptyText,
  page,
  hasMore,
  isFetching,
  onPage,
  testRole,
  toolbar,
  t,
  children,
}: Props) {
  if (isLoading) {
    return (
      <div className={styles.state} data-test-role={`${testRole}-loading`}>
        <Loader type="inline" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.state} data-test-role={`${testRole}-failed`}>
        <AppError
          description={t("list.failed")}
          resolution={t("list.failureSupport")}
        />
      </div>
    );
  }

  return (
    <div className={styles.tab} data-test-role={testRole}>
      {!isEmpty ? toolbar : null}
      {isEmpty ? <EmptyState heading={emptyText} /> : children}
      {page > 0 || hasMore ? (
        <div className={styles.pager}>
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("drawer.page.previous")}
            isDisabled={page === 0 || isFetching}
            onPress={() => onPage(page - 1)}
          />
          <span>{t("drawer.page.label", { page: page + 1 })}</span>
          <Button
            kind="text-only"
            variant="tertiary"
            text={t("drawer.page.next")}
            isDisabled={!hasMore || isFetching}
            onPress={() => onPage(page + 1)}
          />
        </div>
      ) : null}
    </div>
  );
}
